import { docIcon, downloadIcon } from "assets/icons";
import { Button } from "components/form";
import React, { useState } from "react";
import "./DownloadPOD.scss";
import { toast } from "react-toastify";
import { searchOrderPODAPI, downloadPODZipAPI } from "services/user-controllers/order-controller";

const DownloadPOD = () => {
  const [orderInput, setOrderInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [result, setResult] = useState(null);
  const [showPartialConfirm, setShowPartialConfirm] = useState(false);

  const handleSearch = async () => {
    const trimmed = orderInput.trim();
    if (!trimmed) {
      toast.error("Please enter at least one order number or LSP docket number");
      return;
    }
    setLoading(true);
    setResult(null);
    setShowPartialConfirm(false);
    try {
      const { data } = await searchOrderPODAPI(trimmed);
      if (data.code === 200) {
        const { withPOD, withoutPOD, notFound } = data.data;
        setResult({ withPOD, withoutPOD, notFound });

        if (withPOD.length === 0 && withoutPOD.length === 0) {
          toast.error("No matching orders found for your account");
        } else if (withoutPOD.length > 0 && withPOD.length > 0) {
          setShowPartialConfirm(true);
        }
      } else {
        toast.error(data?.message || "Search failed");
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadZip = async (idsToDownload) => {
    setDownloading(true);
    try {
      const { data } = await downloadPODZipAPI(idsToDownload);
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/zip" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `POD_${Date.now()}.zip`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success("POD ZIP downloaded successfully");
      setShowPartialConfirm(false);
    } catch (err) {
      console.error(err);
      toast.error("Download failed. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const allAvailable = result && result.withPOD.length > 0 && result.withoutPOD.length === 0;
  const partialAvailable = result && result.withPOD.length > 0 && result.withoutPOD.length > 0;
  const noneAvailable = result && result.withPOD.length === 0 && result.withoutPOD.length > 0;

  return (
    <div className="download_pod">
      <div className="dashboard_title">
        <h4>Download POD</h4>
      </div>

      {/* Search Box */}
      <div className="pod_search_wrap">
        <div className="search_wrap">
          <img src={docIcon} alt="" />
          <input
            type="search"
            value={orderInput}
            className="input form-control"
            placeholder="Enter Order Number or LSP Docket No (comma separated for multiple)"
            onChange={(e) => setOrderInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
          <Button onClick={handleSearch} disabled={loading}>
            {loading ? "Searching..." : "Search Now"}
          </Button>
        </div>
      </div>

      {/* Results */}
      {result && (
        <div className="pod_results">

          {/* All PODs available */}
          {allAvailable && (
            <div className="pod_status_banner success">
              <span>✓ POD available for all {result.withPOD.length} order(s)</span>
              <Button
                onClick={() => handleDownloadZip(result.withPOD.map(o => o.orderId))}
                disabled={downloading}
              >
                <img src={downloadIcon} alt="" />
                {downloading ? "Downloading..." : "Download All as ZIP"}
              </Button>
            </div>
          )}

          {/* None available */}
          {noneAvailable && (
            <div className="pod_status_banner error">
              <span>✗ No POD available yet for the searched order(s)</span>
            </div>
          )}

          {/* Partial confirmation prompt — shown BEFORE the order lists */}
          {showPartialConfirm && partialAvailable && (
            <div className="pod_partial_confirm">
              <p>
                <strong>{result.withoutPOD.length}</strong> order(s) do not have a POD yet.
                Do you want to download the available <strong>{result.withPOD.length}</strong> POD(s)?
              </p>
              <div className="confirm_actions">
                <Button
                  onClick={() => handleDownloadZip(result.withPOD.map(o => o.orderId))}
                  disabled={downloading}
                >
                  <img src={downloadIcon} alt="" />
                  {downloading ? "Downloading..." : `Download ${result.withPOD.length} Available POD(s)`}
                </Button>
                <button className="cancel_btn" onClick={() => setShowPartialConfirm(false)}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Orders WITH POD */}
          {result.withPOD.length > 0 && (
            <div className="pod_group">
              <h5 className="group_title available">
                POD Available ({result.withPOD.length})
              </h5>
              {result.withPOD.map((order) => (
                <div key={order.orderId} className="pod_card available">
                  <div className="pod_info">
                    <strong>{order.orderId}</strong>
                    {order.consignee && <span>Consignee: {order.consignee}</span>}
                    <span>{order.fromPin} → {order.toPin}</span>
                  </div>
                  <span className="pod_badge available">POD Ready</span>
                </div>
              ))}
            </div>
          )}

          {/* Orders WITHOUT POD */}
          {result.withoutPOD.length > 0 && (
            <div className="pod_group">
              <h5 className="group_title missing">
                POD Not Available ({result.withoutPOD.length})
              </h5>
              {result.withoutPOD.map((order) => (
                <div key={order.orderId} className="pod_card missing">
                  <div className="pod_info">
                    <strong>{order.orderId}</strong>
                    {order.consignee && <span>Consignee: {order.consignee}</span>}
                    <span>{order.fromPin} → {order.toPin}</span>
                  </div>
                  <span className="pod_badge missing">No POD Yet</span>
                </div>
              ))}
            </div>
          )}

          {/* Not found */}
          {result.notFound.length > 0 && (
            <div className="pod_group">
              <h5 className="group_title not_found">
                Orders Not Found ({result.notFound.length})
              </h5>
              <div className="pod_not_found_list">
                {result.notFound.map((id) => (
                  <span key={id} className="not_found_tag">{id}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {!result && !loading && (
        <p className="pod_hint">Enter one or more order numbers or LSP docket numbers (comma separated) to check POD availability</p>
      )}
    </div>
  );
};

export default DownloadPOD;
